/**
 * Blog Post 5: Fixed vs. Floating Rate Mortgages: Which Should You Choose in 2026?
 * 定息按揭 vs 浮息按揭：2026年應該點樣揀？
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import AmazonRecommendation from '../components/common/AmazonRecommendation';
import SEO from '../components/common/SEO';

export default function BlogPost5() {
  const { i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    trackPageView('/blog/fixed-vs-floating-rate-mortgages-hong-kong-2026', 'Blog Post 5');
    document.title = isEnglish
      ? 'Fixed vs. Floating Rate Mortgages: Which Should You Choose in 2026? | PayWise365'
      : '定息按揭 vs 浮息按揭：2026年應該點樣揀？ | PayWise365';
  }, [isEnglish]);

  const seoTitle = isEnglish
    ? 'Fixed vs. Floating Rate Mortgages: Which Should You Choose in 2026? | PayWise365'
    : '定息按揭 vs 浮息按揭：2026年應該點樣揀？ | PayWise365';

  const seoDescription = isEnglish
    ? 'Complete 2026 comparison of fixed and floating rate mortgages in Hong Kong. Understand the pros and cons, when to choose each option, current market trends, and how to make the right decision for your financial situation.'
    : '2026年香港定息與浮息按揭全面比較。了解各自優缺點、選擇時機、市場趨勢，助你根據個人財務狀況作出最佳決策。';

  const seoKeywords = isEnglish
    ? 'fixed rate mortgage Hong Kong, floating rate mortgage, 定息按揭, 浮息按揭, H按, P按, mortgage rate comparison, interest rate trends 2026, mortgage decision guide'
    : '定息按揭, 浮息按揭, H按P按比較, 按揭利率選擇, 2026利率走勢, 按揭決策';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://www.paywise365.com/blog/fixed-vs-floating-rate-mortgages-hong-kong-2026"
        ogType="article"
        publishDate="2026-02-09T00:00:00+08:00"
        modifiedDate="2026-02-09T00:00:00+08:00"
        articleSection={isEnglish ? 'Mortgage Comparison' : '按揭比較'}
        tags={isEnglish ? ['Fixed Rate', 'Floating Rate', 'Mortgage Comparison'] : ['定息', '浮息', '按揭比較']}
      />
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            {isEnglish ? 'Fixed vs. Floating Rate Mortgages: Which Should You Choose in 2026?' : '定息按揭 vs 浮息按揭：2026年應該點樣揀？'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            {isEnglish ? 'A comprehensive comparison to help you make the right choice' : '全面比較幫你做出正確選擇'}
          </Typography>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          component="article"
          sx={{
            lineHeight: 1.8,
            '& p': {
              mb: 2,
            },
            '& h2': {
              mt: 4,
              mb: 2,
              fontSize: '1.75rem',
              fontWeight: 700,
            },
            '& h3': {
              mt: 3,
              mb: 1.5,
              fontSize: '1.5rem',
              fontWeight: 600,
            },
            '& ul, & ol': {
              mb: 2,
              pl: 3,
            },
            '& li': {
              mb: 1,
            },
          }}
        >
          {isEnglish ? (
            // English Content
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    padding: '6px 14px',
                    borderRadius: '14px',
                    fontWeight: '500',
                    marginRight: '12px'
                  }}>
                    Mortgage Strategy
                  </span>
                  <span>Published: February 1, 2026</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>8 min read</span>
                </div>
                <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b', marginTop: '20px', lineHeight: '1.3' }}>
                  Fixed vs. Floating Rate Mortgages: Which Should You Choose in 2026?
                </h1>
                <p style={{ fontSize: '18px', color: '#64748b', marginTop: '15px' }}>
                  One of the most important decisions when getting a mortgage is choosing between fixed and floating rates. Let's break down the pros, cons, and help you decide what's best for your situation.
                </p>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                Understanding the Basics
              </h2>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#334155' }}>
                What is a Floating Rate Mortgage?
              </h3>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                A floating rate mortgage has an interest rate that changes based on market conditions. In Hong Kong, floating rates are typically based on one of two benchmarks:
              </p>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>HIBOR (Hong Kong Interbank Offered Rate):</strong> The interest rate at which banks lend to each other. More volatile but often lower during stable periods.</li>
                <li style={{ marginBottom: '10px' }}><strong>Prime Rate (P):</strong> The base lending rate set by banks. More stable but adjusts less frequently, typically tied to US Federal Reserve rates.</li>
              </ul>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                A typical floating rate mortgage might be quoted as "P-2.5%" or "H+1.3%", meaning Prime minus 2.5% or HIBOR plus 1.3%.
              </p>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#334155' }}>
                What is a Fixed Rate Mortgage?
              </h3>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                A fixed rate mortgage locks in your interest rate for a specific period—typically 1, 2, 3, or 5 years. During this time, your rate won't change regardless of market fluctuations. After the fixed period ends, the mortgage usually converts to a floating rate.
              </p>

              <div style={{
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '30px',
                marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#1e293b' }}>
                  📊 2026 Hong Kong Mortgage Rate Snapshot
                </h3>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8' }}>
                  <li><strong>Floating (HIBOR-based):</strong> H+1.3% to H+1.5% (effective ~3.5-4.0%)</li>
                  <li><strong>Floating (Prime-based):</strong> P-2.5% to P-2.75% (effective ~3.0-3.25%)</li>
                  <li><strong>Fixed 2-year:</strong> 3.125% to 3.5%</li>
                  <li><strong>Fixed 3-year:</strong> 3.375% to 3.75%</li>
                </ul>
                <p style={{ fontSize: '14px', color: '#64748b', marginTop: '15px', marginBottom: 0 }}>
                  *Rates vary by bank, loan size, and borrower profile. Updated February 2026.
                </p>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                Floating Rate Mortgages: Pros and Cons
              </h2>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                ✅ Advantages
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>Lower initial rates:</strong> Floating rates are typically 0.5-1% lower than fixed rates</li>
                <li style={{ marginBottom: '10px' }}><strong>Benefit from rate drops:</strong> If interest rates fall, your payments automatically decrease</li>
                <li style={{ marginBottom: '10px' }}><strong>More flexibility:</strong> Easier to refinance or switch banks without breaking a fixed-rate contract</li>
                <li style={{ marginBottom: '10px' }}><strong>No rate lock-in penalties:</strong> Can make extra repayments or switch anytime</li>
              </ul>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                ❌ Disadvantages
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>Rate uncertainty:</strong> Your monthly payment can increase if rates rise</li>
                <li style={{ marginBottom: '10px' }}><strong>Budgeting challenges:</strong> Harder to plan long-term finances with variable payments</li>
                <li style={{ marginBottom: '10px' }}><strong>HIBOR volatility:</strong> H-based mortgages can spike during financial stress (though there's usually a Prime cap)</li>
                <li style={{ marginBottom: '10px' }}><strong>Stress during rate hikes:</strong> Can be financially painful if rates climb significantly</li>
              </ul>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                Fixed Rate Mortgages: Pros and Cons
              </h2>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                ✅ Advantages
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>Payment certainty:</strong> Know exactly what you'll pay for the fixed period</li>
                <li style={{ marginBottom: '10px' }}><strong>Easy budgeting:</strong> Predictable monthly expenses help with financial planning</li>
                <li style={{ marginBottom: '10px' }}><strong>Protection from rate hikes:</strong> Immune to interest rate increases during the fixed term</li>
                <li style={{ marginBottom: '10px' }}><strong>Peace of mind:</strong> Reduced financial stress and anxiety about rate movements</li>
              </ul>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                ❌ Disadvantages
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>Higher initial cost:</strong> Fixed rates are typically 0.5-1% higher than floating</li>
                <li style={{ marginBottom: '10px' }}><strong>Can't benefit from rate drops:</strong> Stuck with higher rate if market rates fall</li>
                <li style={{ marginBottom: '10px' }}><strong>Break fees:</strong> Expensive penalties (1-3% of outstanding loan) if you refinance during the fixed period</li>
                <li style={{ marginBottom: '10px' }}><strong>Limited to short periods:</strong> Most fixed periods are only 2-3 years, not the full mortgage term</li>
              </ul>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                Real-World Cost Comparison (2026)
              </h2>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                Let's compare the actual costs using a real example:
              </p>
              <div style={{
                background: '#fefce8',
                border: '1px solid #fde047',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '20px',
                marginBottom: '30px'
              }}>
                <p style={{ fontWeight: '600', marginBottom: '15px', color: '#1e293b' }}>Example Scenario:</p>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: 0 }}>
                  <li>Loan Amount: HKD 5,000,000</li>
                  <li>Loan Term: 25 years</li>
                  <li>Floating Rate: 3.5% (H+1.4%, effective rate)</li>
                  <li>Fixed Rate (2-year): 3.875%</li>
                </ul>
              </div>

              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginBottom: '30px',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Mortgage Type</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Monthly Payment</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Total (2 years)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px' }}>Floating (3.5%)</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>HKD 25,016</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>HKD 600,384</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px' }}>Fixed 2-year (3.875%)</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>HKD 26,047</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>HKD 625,128</td>
                  </tr>
                  <tr style={{ background: '#fef2f2' }}>
                    <td style={{ padding: '12px', fontWeight: '600' }}>Difference</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>+HKD 1,031/mo</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>+HKD 24,744</td>
                  </tr>
                </tbody>
              </table>

              <p style={{ marginBottom: '15px', color: '#475569' }}>
                Over the 2-year fixed period, you'd pay approximately <strong>HKD 24,744 more</strong> for the certainty of a fixed rate. This is essentially the "insurance premium" you pay for rate stability.
              </p>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                When to Choose Floating Rate
              </h2>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                A floating rate mortgage makes sense if you:
              </p>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}>Believe interest rates will remain stable or decrease</li>
                <li style={{ marginBottom: '10px' }}>Can comfortably handle payment increases of 10-20%</li>
                <li style={{ marginBottom: '10px' }}>Value flexibility and might refinance or sell within 2-3 years</li>
                <li style={{ marginBottom: '10px' }}>Want to save money in the short term with lower initial rates</li>
                <li style={{ marginBottom: '10px' }}>Are comfortable monitoring rate trends and market conditions</li>
              </ul>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                When to Choose Fixed Rate
              </h2>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                A fixed rate mortgage makes sense if you:
              </p>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}>Are maxed out on your borrowing capacity and can't afford payment increases</li>
                <li style={{ marginBottom: '10px' }}>Expect interest rates to rise significantly in the next 2-3 years</li>
                <li style={{ marginBottom: '10px' }}>Value budgeting certainty and want predictable monthly expenses</li>
                <li style={{ marginBottom: '10px' }}>Are a first-time buyer and want to avoid surprises</li>
                <li style={{ marginBottom: '10px' }}>Plan to stay in your home long-term (10+ years)</li>
              </ul>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                The Hybrid Strategy: Best of Both Worlds?
              </h2>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                Many smart borrowers use a <strong>hybrid approach</strong>:
              </p>
              <ol style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>Start with floating</strong> when rates are low (like now in 2026)</li>
                <li style={{ marginBottom: '10px' }}><strong>Monitor rate trends</strong> closely, especially Federal Reserve signals</li>
                <li style={{ marginBottom: '10px' }}><strong>Lock in a fixed rate</strong> if you see clear signs of rate increases ahead</li>
                <li style={{ marginBottom: '10px' }}><strong>Return to floating</strong> after the rate hike cycle ends</li>
              </ol>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                This requires more active management but can save significant money over the life of your mortgage.
              </p>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                Expert Tips for 2026
              </h2>
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '20px',
                marginBottom: '30px'
              }}>
                <ol style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: 0 }}>
                  <li style={{ marginBottom: '10px' }}><strong>Understand the cap:</strong> Most HIBOR mortgages have a Prime rate cap (e.g., "H+1.3%, cap at P-2.5%"). This protects you if HIBOR spikes.</li>
                  <li style={{ marginBottom: '10px' }}><strong>Negotiate everything:</strong> Banks are competing hard for business. Don't just focus on rate—ask for cashback, waived legal fees, and free valuations.</li>
                  <li style={{ marginBottom: '10px' }}><strong>Check the breakage penalty:</strong> If considering fixed rate, understand the exact penalty formula for early exit.</li>
                  <li style={{ marginBottom: '10px' }}><strong>Stress test both scenarios:</strong> Calculate what happens if rates increase by 2-3% over the next few years.</li>
                  <li style={{ marginBottom: '10px' }}><strong>Review annually:</strong> Set a calendar reminder to review your mortgage strategy every year, especially when approaching the end of a fixed term.</li>
                </ol>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                Frequently Asked Questions
              </h2>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  Q: Can I switch from floating to fixed anytime?
                </h3>
                <p style={{ marginBottom: '0', color: '#475569' }}>
                  Yes, most banks allow you to convert from floating to fixed at any time, though they may charge a small administrative fee (typically HKD 3,000-5,000). However, going from fixed to floating before your term ends will trigger expensive break fees.
                </p>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  Q: What happens when my fixed rate period ends?
                </h3>
                <p style={{ marginBottom: '0', color: '#475569' }}>
                  Your mortgage automatically converts to the bank's standard floating rate. This is a good time to refinance with another bank or negotiate a better rate with your current lender, as they often offer competitive "retention packages" to keep customers.
                </p>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  Q: Is HIBOR or Prime better in 2026?
                </h3>
                <p style={{ marginBottom: '0', color: '#475569' }}>
                  As of February 2026, Prime-based rates (P-2.5% = ~3.0-3.25%) are slightly lower than HIBOR-based rates (H+1.3-1.5% = ~3.5-4.0%). However, HIBOR could be advantageous if US rates decrease. Always compare the effective rate and ensure HIBOR mortgages have a Prime cap for protection.
                </p>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  Q: Should first-time buyers choose fixed or floating?
                </h3>
                <p style={{ marginBottom: '0', color: '#475569' }}>
                  First-time buyers with tight budgets should consider a 2-3 year fixed rate for initial stability while they adjust to homeownership costs. Once you build a financial buffer and understand your cash flow, you can switch to floating to save money.
                </p>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '12px',
                padding: '30px',
                marginTop: '50px',
                textAlign: 'center'
              }}>
                <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '15px', color: 'white' }}>
                  🧮 Ready to Compare Your Options?
                </h3>
                <p style={{ fontSize: '16px', marginBottom: '25px', opacity: 0.95 }}>
                  Use our free mortgage calculator to model different rate scenarios and see how fixed vs. floating affects your monthly payment and total interest costs.
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    background: 'white',
                    color: '#667eea',
                    padding: '14px 32px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '16px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  Try Our Calculator →
                </a>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              <div style={{
                marginTop: '50px',
                paddingTop: '30px',
                borderTop: '1px solid #e2e8f0',
                fontSize: '14px',
                color: '#64748b'
              }}>
                <p style={{ marginBottom: '10px' }}>
                  <strong>Disclaimer:</strong> This article is for informational purposes only and does not constitute financial advice. Mortgage rates and terms vary by lender and individual circumstances. Always consult with a licensed mortgage advisor before making decisions.
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Last Updated:</strong> February 1, 2026
                </p>
              </div>
            </>
          ) : (
            // Cantonese Content (繁體中文)
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff',
                    color: '#2563eb',
                    padding: '6px 14px',
                    borderRadius: '14px',
                    fontWeight: '500',
                    marginRight: '12px'
                  }}>
                    按揭策略
                  </span>
                  <span>發佈日期：2026年2月1日</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>閱讀時間：8分鐘</span>
                </div>
                <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b', marginTop: '20px', lineHeight: '1.3' }}>
                  定息按揭 vs 浮息按揭：2026年應該點樣揀？
                </h1>
                <p style={{ fontSize: '18px', color: '#64748b', marginTop: '15px' }}>
                  喺申請按揭時，揀定息定係浮息係其中一個最重要嘅決定。我哋會詳細分析兩者嘅優缺點，幫你揀到最適合自己嘅選項。
                </p>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                基本概念
              </h2>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#334155' }}>
                咩係浮息按揭？
              </h3>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                浮息按揭嘅利率會隨住市場變化而調整。喺香港，浮息按揭通常基於以下兩種基準：
              </p>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>HIBOR（香港銀行同業拆息率）：</strong>銀行之間互相借貸嘅利率。波動較大，但穩定時期通常較低。</li>
                <li style={{ marginBottom: '10px' }}><strong>最優惠利率（Prime Rate / P）：</strong>銀行自訂嘅基準利率。較為穩定，調整次數較少，通常跟隨美國聯儲局利率。</li>
              </ul>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                典型嘅浮息按揭會寫成「P-2.5%」或者「H+1.3%」，即係最優惠利率減2.5%或者HIBOR加1.3%。
              </p>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '30px', marginBottom: '15px', color: '#334155' }}>
                咩係定息按揭？
              </h3>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                定息按揭會將你嘅利率鎖定喺指定期間——通常係1年、2年、3年或者5年。喺呢段期間，無論市場點樣變化，你嘅利率都唔會變。定息期完咗之後，按揭通常會自動轉為浮息。
              </p>

              <div style={{
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '30px',
                marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#1e293b' }}>
                  📊 2026年香港按揭利率概況
                </h3>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8' }}>
                  <li><strong>浮息（HIBOR）：</strong>H+1.3% 至 H+1.5%（實際利率約3.5-4.0%）</li>
                  <li><strong>浮息（最優惠利率）：</strong>P-2.5% 至 P-2.75%（實際利率約3.0-3.25%）</li>
                  <li><strong>2年定息：</strong>3.125% 至 3.5%</li>
                  <li><strong>3年定息：</strong>3.375% 至 3.75%</li>
                </ul>
                <p style={{ fontSize: '14px', color: '#64748b', marginTop: '15px', marginBottom: 0 }}>
                  *利率因銀行、貸款額同借款人而異。更新日期：2026年2月
                </p>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                浮息按揭：優點同缺點
              </h2>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                ✅ 優點
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>初期利率較低：</strong>浮息通常比定息低0.5-1%</li>
                <li style={{ marginBottom: '10px' }}><strong>受惠於減息：</strong>如果利率下跌，你嘅供款會自動減少</li>
                <li style={{ marginBottom: '10px' }}><strong>靈活性高：</strong>可以隨時轉按或者轉銀行，無需承擔違約金</li>
                <li style={{ marginBottom: '10px' }}><strong>無鎖定期罰款：</strong>可以隨時做額外還款或者轉銀行</li>
              </ul>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                ❌ 缺點
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>利率不確定：</strong>如果利率上升，每月供款會增加</li>
                <li style={{ marginBottom: '10px' }}><strong>難以規劃預算：</strong>供款會變動，長遠財務計劃較難</li>
                <li style={{ marginBottom: '10px' }}><strong>HIBOR波動：</strong>H按喺金融壓力時可能急升（雖然通常有P按封頂保障）</li>
                <li style={{ marginBottom: '10px' }}><strong>加息壓力：</strong>如果利率大幅上升，財務壓力會好大</li>
              </ul>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                定息按揭：優點同缺點
              </h2>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                ✅ 優點
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>供款確定：</strong>喺定息期內清楚知道每個月要供幾多</li>
                <li style={{ marginBottom: '10px' }}><strong>易於預算：</strong>可預測嘅每月開支，方便財務規劃</li>
                <li style={{ marginBottom: '10px' }}><strong>免受加息影響：</strong>定息期內完全唔怕加息</li>
                <li style={{ marginBottom: '10px' }}><strong>心理安穩：</strong>減少對利率波動嘅擔憂同壓力</li>
              </ul>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                ❌ 缺點
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>初期成本較高：</strong>定息利率通常比浮息高0.5-1%</li>
                <li style={{ marginBottom: '10px' }}><strong>唔能受惠減息：</strong>如果市場利率下跌，你都要繼續供較高利率</li>
                <li style={{ marginBottom: '10px' }}><strong>違約金：</strong>定息期內轉按要罰款（通常係未還本金嘅1-3%）</li>
                <li style={{ marginBottom: '10px' }}><strong>鎖定期短：</strong>大部分定息只係2-3年，唔係成個按揭期</li>
              </ul>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                實際成本比較（2026年）
              </h2>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                我哋用實際例子嚟比較下兩者嘅成本：
              </p>
              <div style={{
                background: '#fefce8',
                border: '1px solid #fde047',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '20px',
                marginBottom: '30px'
              }}>
                <p style={{ fontWeight: '600', marginBottom: '15px', color: '#1e293b' }}>例子情境：</p>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: 0 }}>
                  <li>貸款額：500萬港元</li>
                  <li>還款期：25年</li>
                  <li>浮息：3.5%（H+1.4%，實際利率）</li>
                  <li>2年定息：3.875%</li>
                </ul>
              </div>

              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginBottom: '30px',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>按揭類型</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>每月供款</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>2年總供款</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px' }}>浮息（3.5%）</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>$25,016</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>$600,384</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px' }}>2年定息（3.875%）</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>$26,047</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>$625,128</td>
                  </tr>
                  <tr style={{ background: '#fef2f2' }}>
                    <td style={{ padding: '12px', fontWeight: '600' }}>差額</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>+$1,031/月</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>+$24,744</td>
                  </tr>
                </tbody>
              </table>

              <p style={{ marginBottom: '15px', color: '#475569' }}>
                喺2年定息期內，你會額外支付大約<strong>$24,744</strong>嚟換取利率穩定。呢個可以睇成係為咗避免利率波動而支付嘅「保險費」。
              </p>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                幾時應該揀浮息
              </h2>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                如果你有以下情況，浮息按揭會較適合：
              </p>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}>你認為利率會維持穩定或者下跌</li>
                <li style={{ marginBottom: '10px' }}>你有能力應付供款增加10-20%</li>
                <li style={{ marginBottom: '10px' }}>你睇重靈活性，可能喺2-3年內轉按或賣樓</li>
                <li style={{ marginBottom: '10px' }}>你想喺短期內節省較低嘅利息成本</li>
                <li style={{ marginBottom: '10px' }}>你願意留意利率走勢同市場變化</li>
              </ul>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                幾時應該揀定息
              </h2>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                如果你有以下情況，定息按揭會較適合：
              </p>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}>你借貸能力已經用到盡，無法負擔供款增加</li>
                <li style={{ marginBottom: '10px' }}>你預期未來2-3年利率會大幅上升</li>
                <li style={{ marginBottom: '10px' }}>你重視預算確定性，想要可預測嘅每月開支</li>
                <li style={{ marginBottom: '10px' }}>你係首次置業，想避免意外</li>
                <li style={{ marginBottom: '10px' }}>你計劃長期持有物業（10年以上）</li>
              </ul>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                混合策略：兩全其美？
              </h2>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                好多精明嘅業主會採用<strong>混合策略</strong>：
              </p>
              <ol style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li style={{ marginBottom: '10px' }}><strong>喺低息期用浮息</strong>（好似2026年依家咁）</li>
                <li style={{ marginBottom: '10px' }}><strong>密切留意利率走勢</strong>，特別係美國聯儲局嘅訊號</li>
                <li style={{ marginBottom: '10px' }}><strong>如果見到明顯加息訊號就轉定息</strong></li>
                <li style={{ marginBottom: '10px' }}><strong>等加息周期完結後再轉返浮息</strong></li>
              </ol>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                呢個做法需要較主動管理，但可以喺按揭期內節省唔少錢。
              </p>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                2026年專家貼士
              </h2>
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '20px',
                marginBottom: '30px'
              }}>
                <ol style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: 0 }}>
                  <li style={{ marginBottom: '10px' }}><strong>了解封頂條款：</strong>大部分H按都有P按封頂（例如「H+1.3%，上限P-2.5%」），呢個可以保護你免受HIBOR急升影響。</li>
                  <li style={{ marginBottom: '10px' }}><strong>所有嘢都可以傾：</strong>銀行而家競爭好激烈，唔好淨係講利率——要求現金回贈、豁免律師費、免費估價。</li>
                  <li style={{ marginBottom: '10px' }}><strong>睇清楚罰息條款：</strong>如果考慮定息，要清楚了解提早解約嘅罰款計算方法。</li>
                  <li style={{ marginBottom: '10px' }}><strong>兩種情境都要壓測：</strong>計算如果未來幾年利率升2-3%會點。</li>
                  <li style={{ marginBottom: '10px' }}><strong>每年檢討：</strong>設定提醒每年檢討你嘅按揭策略，特別係定息期快完嘅時候。</li>
                </ol>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                常見問題
              </h2>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  問：可唔可以隨時由浮息轉定息？
                </h3>
                <p style={{ marginBottom: '0', color: '#475569' }}>
                  可以，大部分銀行都容許你隨時由浮息轉定息，不過可能要收少少行政費（通常$3,000-5,000）。但係如果定息期未完就轉返浮息，就要支付好貴嘅違約金。
                </p>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  問：定息期完咗之後會點？
                </h3>
                <p style={{ marginBottom: '0', color: '#475569' }}>
                  你嘅按揭會自動轉為銀行嘅標準浮息。呢個係轉按去其他銀行或者同現有銀行傾更好利率嘅好時機，因為佢哋通常會提供有競爭力嘅「挽留優惠」嚟留住客戶。
                </p>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  問：2026年揀HIBOR定P按好啲？
                </h3>
                <p style={{ marginBottom: '0', color: '#475569' }}>
                  截至2026年2月，P按（P-2.5% = 約3.0-3.25%）比H按（H+1.3-1.5% = 約3.5-4.0%）稍低。不過如果美國減息，H按可能會更有利。記住要比較實際利率，同埋確保H按有P按封頂保障。
                </p>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px', color: '#334155' }}>
                  問：首次置業應該揀定息定浮息？
                </h3>
                <p style={{ marginBottom: '0', color: '#475569' }}>
                  預算緊絀嘅首次置業人士可以考慮2-3年定息，喺適應供樓成本期間有較穩定嘅支出。等你建立咗財政儲備同了解咗現金流之後，就可以轉浮息去慳錢。
                </p>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '12px',
                padding: '30px',
                marginTop: '50px',
                textAlign: 'center'
              }}>
                <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '15px', color: 'white' }}>
                  🧮 準備好比較唔同選項？
                </h3>
                <p style={{ fontSize: '16px', marginBottom: '25px', opacity: 0.95 }}>
                  用我哋嘅免費按揭計算機模擬唔同利率情境，睇下定息同浮息點樣影響你嘅每月供款同總利息成本。
                </p>
                <a
                  href="/"
                  style={{
                    display: 'inline-block',
                    background: 'white',
                    color: '#667eea',
                    padding: '14px 32px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '16px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  試用我哋嘅計算機 →
                </a>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              <div style={{
                marginTop: '50px',
                paddingTop: '30px',
                borderTop: '1px solid #e2e8f0',
                fontSize: '14px',
                color: '#64748b'
              }}>
                <p style={{ marginBottom: '10px' }}>
                  <strong>免責聲明：</strong>本文僅供參考，並不構成財務建議。按揭利率同條款因銀行同個人情況而異。做任何決定前請諮詢持牌按揭顧問。
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>最後更新：</strong>2026年2月1日
                </p>
              </div>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}
